import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'reactstrap';
import { REZRATE_VIDEO_IFRAME_ALLOW } from 'Services/rezrateVideoShell';

const ShellCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 100%;
  border: 1px solid rgb(51, 122, 183);
  border-radius: 4px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
`;

const ShellToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgb(51, 122, 183);
  color: #ffffff;
`;

const ShellTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ShellTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShellSubtitle = styled.div`
  font-size: 0.8rem;
  opacity: 0.95;
`;

const ShellActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ShellFrameArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 520px;
  background: linear-gradient(180deg, #f7fbff 0%, #eef4fb 100%);

  @media (max-width: 1100px) {
    min-height: 420px;
  }

  @media (max-width: 700px) {
    min-height: 320px;
  }
`;

const ShellOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: #315f8d;
  background: rgba(247, 251, 255, 0.92);
  z-index: 1;
`;

const ShellMessage = styled.div`
  max-width: 520px;
`;

const ShellMessageTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 8px;
`;

const ShellMessageBody = styled.div`
  font-size: 0.95rem;
  line-height: 1.45;
`;

const VideoIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  background: #ffffff;
`;

const RezrateVideoShell = ({
  emptyDescription = 'Choose a meeting to load the video shell.',
  emptyTitle = 'No Meeting Selected',
  iframeSrc = '',
  mode = 'embedded',
  onPopOut = null,
  onReturnEmbedded = null,
  title = 'RezRATE Meeting',
}) => {
  const [status, setStatus] = useState(iframeSrc ? 'loading' : 'idle');

  useEffect(() => {
    setStatus(iframeSrc ? 'loading' : 'idle');
  }, [iframeSrc]);

  if (mode === 'poppedOut') {
    return (
      <ShellCard>
        <ShellToolbar>
          <ShellTitleGroup>
            <ShellTitle>{title || 'RezRATE Meeting'}</ShellTitle>
            <ShellSubtitle>Meeting popped out</ShellSubtitle>
          </ShellTitleGroup>
          <ShellActions>
            {typeof onReturnEmbedded === 'function' ? (
              <Button color="light" size="sm" onClick={onReturnEmbedded}>
                Bring Back Here
              </Button>
            ) : null}
            {typeof onPopOut === 'function' ? (
              <Button color="light" outline size="sm" onClick={onPopOut}>
                Focus Popup
              </Button>
            ) : null}
          </ShellActions>
        </ShellToolbar>
        <ShellFrameArea>
          <ShellOverlay style={{ position: 'static', background: 'transparent' }}>
            <ShellMessage>
              <ShellMessageTitle>Meeting is open in a popup window.</ShellMessageTitle>
              <ShellMessageBody>
                Rejoin from the schedule to reset back to embedded mode, or use &quot;Bring Back Here&quot; to close the
                popup and continue in this page.
              </ShellMessageBody>
            </ShellMessage>
          </ShellOverlay>
        </ShellFrameArea>
      </ShellCard>
    );
  }

  return (
    <ShellCard>
      <ShellToolbar>
        <ShellTitleGroup>
          <ShellTitle>{title || 'RezRATE Meeting'}</ShellTitle>
        </ShellTitleGroup>
        <ShellActions>
          {typeof onPopOut === 'function' && mode !== 'standalone' ? (
            <Button color="light" size="sm" onClick={onPopOut}>
              Pop Out
            </Button>
          ) : null}
        </ShellActions>
      </ShellToolbar>
      <ShellFrameArea>
        {!iframeSrc ? (
          <ShellOverlay>
            <ShellMessage>
              <ShellMessageTitle>{emptyTitle}</ShellMessageTitle>
              <ShellMessageBody>{emptyDescription}</ShellMessageBody>
            </ShellMessage>
          </ShellOverlay>
        ) : null}
        {iframeSrc && status !== 'ready' ? (
          <ShellOverlay>
            <ShellMessage>
              <ShellMessageTitle>Loading meeting...</ShellMessageTitle>
              <ShellMessageBody>
                RezRATE is preparing the video shell. Camera and microphone permissions may prompt after the meeting
                loads.
              </ShellMessageBody>
            </ShellMessage>
          </ShellOverlay>
        ) : null}
        {iframeSrc ? (
          <VideoIframe
            allow={REZRATE_VIDEO_IFRAME_ALLOW}
            allowFullScreen
            onLoad={() => setStatus('ready')}
            src={iframeSrc}
            title={title || 'RezRATE Meeting'}
          />
        ) : null}
      </ShellFrameArea>
    </ShellCard>
  );
};

export default RezrateVideoShell;
